import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { BlogCategory, BlogsService, BlogStatus } from '../../../../services/blog';


interface AdminBlogRow {
  id: number;
  slug: string;
  icon: string | null;
  image: string | null;
  imageUrl: string | null;

  category: BlogCategory | string;
  categoryNameAr: string | null;
  categoryNameEn: string | null;

  titleAr: string;
  titleEn: string;
  excerptAr: string | null;
  excerptEn: string | null;
  readTimeAr: string | null;
  readTimeEn: string | null;
  tagsAr: string[];
  tagsEn: string[];

  status: BlogStatus;
  isFeatured: boolean;
  viewsCount: number;

  publishedAt: string | null;
  createdAt: string | null;

  metaTitleAr: string | null;
  metaTitleEn: string | null;
  metaDescriptionAr: string | null;
  metaDescriptionEn: string | null;
}

@Component({
  selector: 'app-allblogs',
  templateUrl: './allblogs.component.html',
  styleUrl: './allblogs.component.scss',
})
export class AllblogsComponent implements OnInit {
  isAr = true;

  blogs: AdminBlogRow[] = [];
  allBlogs: AdminBlogRow[] = [];

  isLoading = false;
  errorMessage = '';

  searchKeyword = '';
  categoryFilter: BlogCategory | '' = '';
  statusFilter: BlogStatus | '' = '';

  page = 1;
  perPage = 10;
  lastPage = 1;
  total = 0;
  pages: number[] = [];

  showEditModal = false;
  isSaving = false;
  isLoadingDetails = false;
  isDeletingId: number | null = null;
  isChangingStatusId: number | null = null;

  activeBlog: AdminBlogRow | null = null;
  blogForm!: FormGroup;

  selectedImageFile: File | null = null;
  imagePreviewUrl: string | null = null;

  constructor(
    private blogsService: BlogsService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.isAr = localStorage.getItem('language') === 'ar' || true;
    this.createForm();
    this.loadBlogs(1);
  }

  createForm(): void {
    this.blogForm = this.fb.group({
      title_en: ['', Validators.required],
      title_ar: ['', Validators.required],
      slug: [''],

      category: ['career', Validators.required],
      category_en: [''],
      category_ar: [''],
      icon: [''],

      excerpt_en: [''],
      excerpt_ar: [''],

      content_en: [''],
      content_ar: [''],

      tags_en: [''],
      tags_ar: [''],

      read_time_en: [''],
      read_time_ar: [''],

      published_at: [''],
      status: ['published', Validators.required],
      is_featured: [false],

      meta_title_en: [''],
      meta_title_ar: [''],
      meta_description_en: [''],
      meta_description_ar: [''],
    });
  }

  loadBlogs(page: number = 1): void {
    this.page = page;
    this.isLoading = true;
    this.errorMessage = '';

    const filters: any = {};

    if (this.categoryFilter && this.categoryFilter !== 'all') {
      filters.category = this.categoryFilter;
    }

    if (this.searchKeyword.trim()) {
      filters.search = this.searchKeyword.trim();
    }

    /**
     * لو الباك إند بيدعم فلتر status للـ admin هيشتغل.
     * لو مش بيدعمه، الكود تحت بيعمل فلترة محلية كمان.
     */
    if (this.statusFilter) {
      filters.status = this.statusFilter;
    }

    forkJoin({
      ar: this.blogsService.getBlogs({ ...filters, lang: 'ar' }),
      en: this.blogsService.getBlogs({ ...filters, lang: 'en' }),
    }).subscribe({
      next: (res) => {
        const arBlogs = this.extractBlogs(res.ar, 'ar');
        const enBlogs = this.extractBlogs(res.en, 'en');

        this.allBlogs = this.mergeBlogs(arBlogs, enBlogs);

        if (this.statusFilter) {
          this.allBlogs = this.allBlogs.filter(
            (blog) => blog.status === this.statusFilter
          );
        }

        this.total = this.allBlogs.length;
        this.lastPage = Math.max(1, Math.ceil(this.total / this.perPage));
        this.pages = Array.from({ length: this.lastPage }, (_, i) => i + 1);

        this.setCurrentPage(this.page);
        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = 'حدث خطأ أثناء تحميل المقالات.';
        this.isLoading = false;
      },
    });
  }

  extractBlogs(res: any, lang: 'ar' | 'en'): any[] {
    const list: any[] = [];

    if (res?.featured_blog) {
      list.push(res.featured_blog);
    }

    if (Array.isArray(res?.blogs)) {
      list.push(...res.blogs);
    }

    const unique = new Map<string | number, any>();

    list.forEach((item) => {
      const key = item.id || item.slug;

      unique.set(key, {
        ...item,
        __lang: lang,
      });
    });

    return Array.from(unique.values());
  }

  mergeBlogs(arBlogs: any[], enBlogs: any[]): AdminBlogRow[] {
    const map = new Map<number | string, AdminBlogRow>();

    arBlogs.forEach((blog) => {
      const key = blog.id || blog.slug;

      map.set(key, {
        id: blog.id,
        slug: blog.slug,
        icon: blog.icon || null,
        image: blog.image || null,
        imageUrl: blog.image_url || null,

        category: blog.category || '',
        categoryNameAr: blog.category_name || null,
        categoryNameEn: null,

        titleAr: blog.title || '',
        titleEn: '',
        excerptAr: blog.excerpt || null,
        excerptEn: null,
        readTimeAr: blog.read_time || null,
        readTimeEn: null,
        tagsAr: Array.isArray(blog.tags) ? blog.tags : [],
        tagsEn: [],

        status: blog.status || 'published',
        isFeatured: !!blog.is_featured,
        viewsCount: blog.views_count || 0,

        publishedAt: blog.published_at || null,
        createdAt: blog.created_at || null,

        metaTitleAr: blog.meta_title || null,
        metaTitleEn: null,
        metaDescriptionAr: blog.meta_description || null,
        metaDescriptionEn: null,
      });
    });

    enBlogs.forEach((blog) => {
      const key = blog.id || blog.slug;
      const existing = map.get(key);

      if (existing) {
        existing.titleEn = blog.title || '';
        existing.excerptEn = blog.excerpt || null;
        existing.readTimeEn = blog.read_time || null;
        existing.tagsEn = Array.isArray(blog.tags) ? blog.tags : [];
        existing.categoryNameEn = blog.category_name || null;
        existing.metaTitleEn = blog.meta_title || null;
        existing.metaDescriptionEn = blog.meta_description || null;

        existing.imageUrl = existing.imageUrl || blog.image_url || null;
        existing.image = existing.image || blog.image || null;
        existing.icon = existing.icon || blog.icon || null;
      } else {
        map.set(key, {
          id: blog.id,
          slug: blog.slug,
          icon: blog.icon || null,
          image: blog.image || null,
          imageUrl: blog.image_url || null,

          category: blog.category || '',
          categoryNameAr: null,
          categoryNameEn: blog.category_name || null,

          titleAr: '',
          titleEn: blog.title || '',
          excerptAr: null,
          excerptEn: blog.excerpt || null,
          readTimeAr: null,
          readTimeEn: blog.read_time || null,
          tagsAr: [],
          tagsEn: Array.isArray(blog.tags) ? blog.tags : [],

          status: blog.status || 'published',
          isFeatured: !!blog.is_featured,
          viewsCount: blog.views_count || 0,

          publishedAt: blog.published_at || null,
          createdAt: blog.created_at || null,

          metaTitleAr: null,
          metaTitleEn: blog.meta_title || null,
          metaDescriptionAr: null,
          metaDescriptionEn: blog.meta_description || null,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => b.id - a.id);
  }

  setCurrentPage(page: number): void {
    this.page = Math.min(Math.max(page, 1), this.lastPage);

    const start = (this.page - 1) * this.perPage;
    const end = start + this.perPage;

    this.blogs = this.allBlogs.slice(start, end);
  }

  onSearchInput(event: Event): void {
    this.searchKeyword = (event.target as HTMLInputElement).value;
  }

  onCategoryFilterChange(event: Event): void {
    this.categoryFilter = (event.target as HTMLSelectElement).value as BlogCategory | '';
  }

  onStatusFilterChange(event: Event): void {
    this.statusFilter = (event.target as HTMLSelectElement).value as BlogStatus | '';
  }

  resetFilters(): void {
    this.searchKeyword = '';
    this.categoryFilter = '';
    this.statusFilter = '';
    this.loadBlogs(1);
  }

  openEditModal(blog: AdminBlogRow): void {
    this.activeBlog = blog;
    this.showEditModal = true;
    this.selectedImageFile = null;
    this.imagePreviewUrl = blog.imageUrl;

    this.blogForm.reset({
      title_en: blog.titleEn || '',
      title_ar: blog.titleAr || '',
      slug: blog.slug || '',

      category: blog.category || 'career',
      category_en: blog.categoryNameEn || '',
      category_ar: blog.categoryNameAr || '',
      icon: blog.icon || '',

      excerpt_en: blog.excerptEn || '',
      excerpt_ar: blog.excerptAr || '',

      content_en: '',
      content_ar: '',

      tags_en: blog.tagsEn.join(', '),
      tags_ar: blog.tagsAr.join(', '),

      read_time_en: blog.readTimeEn || '',
      read_time_ar: blog.readTimeAr || '',

      published_at: this.toDateInput(blog.publishedAt),
      status: blog.status || 'published',
      is_featured: blog.isFeatured,

      meta_title_en: blog.metaTitleEn || '',
      meta_title_ar: blog.metaTitleAr || '',
      meta_description_en: blog.metaDescriptionEn || '',
      meta_description_ar: blog.metaDescriptionAr || '',
    });

    this.loadBlogDetailsForEdit(blog.slug);
  }

  loadBlogDetailsForEdit(slug: string): void {
    this.isLoadingDetails = true;

    forkJoin({
      ar: this.blogsService.getBlogBySlug(slug, 'ar'),
      en: this.blogsService.getBlogBySlug(slug, 'en'),
    }).subscribe({
      next: (res) => {
        const arBlog = res.ar?.blog;
        const enBlog = res.en?.blog;

        this.blogForm.patchValue({
          content_ar: arBlog?.content || '',
          content_en: enBlog?.content || '',

          title_ar: arBlog?.title || this.blogForm.value.title_ar,
          title_en: enBlog?.title || this.blogForm.value.title_en,

          excerpt_ar: arBlog?.excerpt || this.blogForm.value.excerpt_ar,
          excerpt_en: enBlog?.excerpt || this.blogForm.value.excerpt_en,

          read_time_ar: arBlog?.read_time || this.blogForm.value.read_time_ar,
          read_time_en: enBlog?.read_time || this.blogForm.value.read_time_en,

          tags_ar: Array.isArray(arBlog?.tags)
            ? arBlog.tags.join(', ')
            : this.blogForm.value.tags_ar,

          tags_en: Array.isArray(enBlog?.tags)
            ? enBlog.tags.join(', ')
            : this.blogForm.value.tags_en,
        });

        this.isLoadingDetails = false;
      },
      error: () => {
        this.isLoadingDetails = false;
      },
    });
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.activeBlog = null;
    this.selectedImageFile = null;
    this.imagePreviewUrl = null;
    this.blogForm.reset();
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    this.selectedImageFile = file;
    this.imagePreviewUrl = URL.createObjectURL(file);
  }

  removeSelectedImage(): void {
    this.selectedImageFile = null;
    this.imagePreviewUrl = this.activeBlog?.imageUrl || null;
  }

  submitEdit(): void {
    if (!this.activeBlog || this.blogForm.invalid) {
      this.blogForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;

    const payload = {
      ...this.blogForm.value,
      is_featured: this.blogForm.value.is_featured ? 1 : 0,
    };

    if (!payload.slug) {
      delete payload.slug;
    }

    const formData = this.blogsService.buildBlogFormData(payload);

    if (this.selectedImageFile) {
      formData.append('image', this.selectedImageFile);
    }

    this.blogsService
      .updateBlogWithFormData(this.activeBlog.id, formData)
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.closeEditModal();
          this.loadBlogs(this.page);
        },
        error: () => {
          this.isSaving = false;
          alert('حدث خطأ أثناء حفظ التعديلات.');
        },
      });
  }

  changeStatus(blog: AdminBlogRow, event: Event): void {
    const status = (event.target as HTMLSelectElement).value as BlogStatus;

    this.isChangingStatusId = blog.id;

    const formData = this.blogsService.buildBlogFormData({
      status,
    });

    this.blogsService.updateBlogWithFormData(blog.id, formData).subscribe({
      next: () => {
        blog.status = status;
        this.isChangingStatusId = null;
      },
      error: () => {
        this.isChangingStatusId = null;
        alert('حدث خطأ أثناء تغيير الحالة.');
        this.loadBlogs(this.page);
      },
    });
  }

  toggleFeatured(blog: AdminBlogRow): void {
    this.isChangingStatusId = blog.id;

    const formData = this.blogsService.buildBlogFormData({
      is_featured: blog.isFeatured ? 0 : 1,
    });

    this.blogsService.updateBlogWithFormData(blog.id, formData).subscribe({
      next: () => {
        this.isChangingStatusId = null;
        this.loadBlogs(this.page);
      },
      error: () => {
        this.isChangingStatusId = null;
        alert('حدث خطأ أثناء تغيير حالة المقال المميز.');
      },
    });
  }

  deleteBlog(blog: AdminBlogRow): void {
    const confirmed = confirm(`هل أنت متأكد من حذف المقال: ${this.displayTitle(blog)}؟`);

    if (!confirmed) {
      return;
    }

    this.isDeletingId = blog.id;

    this.blogsService.deleteBlog(blog.id).subscribe({
      next: () => {
        this.isDeletingId = null;
        this.loadBlogs(this.page);
      },
      error: () => {
        this.isDeletingId = null;
        alert('حدث خطأ أثناء حذف المقال.');
      },
    });
  }

  displayTitle(blog: AdminBlogRow): string {
    return blog.titleAr || blog.titleEn || '-';
  }

  displayExcerpt(blog: AdminBlogRow): string {
    return blog.excerptAr || blog.excerptEn || 'لا يوجد ملخص.';
  }

  getCategoryLabel(category: string): string {
    switch (category) {
      case 'career':
        return 'المسار المهني';
      case 'cv':
        return 'السيرة الذاتية';
      case 'travel':
        return 'السفر';
      case 'interview':
        return 'المقابلات';
      default:
        return '-';
    }
  }

  getStatusLabel(status: string): string {
    return status === 'published' ? 'منشور' : 'مسودة';
  }

  getBlogIcon(blog: AdminBlogRow): string {
    return blog.icon || '📝';
  }

  onBlogImageError(blog: AdminBlogRow): void {
    blog.imageUrl = null;
  }

  formatDate(date: string | null): string {
    if (!date) {
      return '-';
    }

    return new Date(date).toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  toDateInput(date: string | null): string {
    if (!date) {
      return '';
    }

    return new Date(date).toISOString().slice(0, 10);
  }

  trackByBlog(index: number, blog: AdminBlogRow): number {
    return blog.id;
  }
}